import { useEffect, useState } from "react";
import { MagnifyingGlass, ArrowsClockwise, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function VerificationLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, found: 0, not_found: 0, last_24h: 0 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [a, b] = await Promise.all([
        api.get("/admin/verification-logs", { params: { q: q || undefined } }),
        api.get("/admin/verification-stats"),
      ]);
      setLogs(a.data.logs);
      setStats(b.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kayıtlar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="p-6 lg:p-10" data-testid="logs-page">
      <span className="label-eyebrow">/ Doğrulama Kayıtları</span>
      <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3">
        Kim hangi sertifikayı sorguladı
      </h1>
      <p className="text-slate-600 mt-2 max-w-2xl">
        Tüm halka açık doğrulama sorguları kayıt altına alınır. Denetim izi ve
        hangi sertifikaların sorgulandığını görmek için kullanışlıdır.
      </p>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
        <Kpi label="Toplam Sorgu" value={stats.total} />
        <Kpi label="Bulundu" value={stats.found} accent="text-emerald-700" />
        <Kpi label="Bulunamadı" value={stats.not_found} accent="text-red-700" />
        <Kpi label="Son 24 Saat" value={stats.last_24h} accent="text-[#0B2046]" />
      </div>

      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
          className="flex flex-1 min-w-[280px] max-w-xl"
        >
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="logs-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Sertifika numarasıyla filtrele…"
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2046] focus:border-[#0B2046]"
            />
          </div>
          <button className="px-4 bg-[#0B2046] text-white font-medium text-sm">Ara</button>
        </form>
        <button
          onClick={load}
          className="px-3 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2 text-sm"
        >
          <ArrowsClockwise size={14} /> Yenile
        </button>
      </div>

      <div className="mt-6 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <Th>Sorgu Zamanı</Th>
              <Th>Sertifika №</Th>
              <Th>Sonuç</Th>
              <Th>IP</Th>
              <Th>Referrer</Th>
              <Th>Tarayıcı</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center label-eyebrow">Yükleniyor…</td></tr>
            )}
            {!loading && logs.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center label-eyebrow">Henüz sorgu yok</td></tr>
            )}
            {logs.map((l) => (
              <tr key={l.id} className="border-t border-slate-200 hover:bg-slate-50">
                <Td mono>{new Date(l.queried_at).toLocaleString("tr-TR")}</Td>
                <Td mono>{l.certificate_number}</Td>
                <Td>
                  {l.found ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-mono-plex text-[10px] tracking-wider">
                      <CheckCircle size={12} weight="fill" /> {l.status}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-red-700 font-mono-plex text-[10px] tracking-wider">
                      <XCircle size={12} weight="fill" /> BULUNAMADI
                    </span>
                  )}
                </Td>
                <Td mono>{l.ip || "-"}</Td>
                <Td className="text-xs truncate max-w-[180px]">{l.referrer || "-"}</Td>
                <Td className="text-xs truncate max-w-[260px]">{l.user_agent || "-"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent = "text-slate-900" }) {
  return (
    <div className="bg-white p-6">
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-3 font-display text-4xl font-black tracking-tighter font-mono-plex ${accent}`}>
        {value}
      </div>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left font-mono-plex font-medium text-[11px] uppercase tracking-wider px-4 py-3">
      {children}
    </th>
  );
}

function Td({ children, mono, className = "" }) {
  return (
    <td className={`px-4 py-3 ${mono ? "font-mono-plex" : ""} text-slate-800 ${className}`}>
      {children}
    </td>
  );
}
